import { useEffect, useRef, useState } from 'react';
import { QUOTE_REJECT, fmtDay, fmtTime } from '../campaign';
import { haptic } from '../../Helper/native';
import chestc from '../assets/chestc.webp';

/* ------------------------------------------------------------------
   "Bonus rewards" · Quote & Claim on X (prototype .card.xcard + #xfab).
   Follow GoSats on X, quote our post, paste the post's link here; once
   the review team approves it, a Golden Chest can be claimed. The card
   follows `state.status`:
   - idle     : "Free Chest" chip, countdown pill on the top edge, "Get
                started" (opens the post on X) and "Paste your link", which
                morphs into an inline field with Submit and a what's-wrong hint
   - review   : "In review" chip, the review-by time and the submitted link
   - done     : "Approved" chip, "Complete, Thank you!", "Claim Golden Chest"
   - claimed  : "Claimed" chip, "Golden Chest added · opens 13 Nov", inert
   - rejected : "Rejected" chip, why (with a fix-it link), then as idle
   Past `quote.endsAt` an idle / rejected card reads "This task has ended"
   with a "Notify me about new tasks" bell instead.
   QuoteFab is the hub's floating pill that brings the card into view
   while it is scrolled away.

   `quote` is the mapped API object (endsAt, postUrl, profileUrl, handle,
   copy); `state` the user's standing; `notify` the "new tasks" reminder;
   `drawOn` the reveal day; `ended` whether the campaign is over (no
   countdown then); `pulse` flashes the card (and opens the field once
   "Get started" was used) when it changes — the FAB's landing.
   `onSubmit(link)` is the API call (rejects with the backend's reason,
   shown in the hint); `onNotify(on)` toggles the reminder; `onClaim()`
   opens the claim moment. `innerRef` receives the card node.
   ------------------------------------------------------------------ */

const DAY = 864e5;
const FLIP_MS = 2600; // the countdown shows the closing date for this long after a tap

/* an X post link — x.com or twitter.com, a handle, "status", the id (prototype X_RE) */
export const X_POST_RE = /^(https?:\/\/)?(www\.|mobile\.)?(x|twitter)\.com\/[A-Za-z0-9_]{1,15}\/status\/\d+/i;

/* what's wrong with a pasted link — null when it's an X post link */
export function linkError(v) {
  if (X_POST_RE.test(v)) return null;
  let u = null;
  try { u = new URL(/^[a-z]+:\/\//i.test(v) ? v : `https://${v}`); } catch (e) { /* not a URL at all */ }
  const host = u && u.hostname.includes('.') && !/\s/.test(v) ? u.hostname.replace(/^(www|m|mobile)\./, '').toLowerCase() : null;
  if (!host) return { kind: 'text', text: 'That\u2019s not a link. Copy the link to your quote post on X and paste it here.' };
  if (host !== 'x.com' && host !== 'twitter.com') return { kind: 'domain', text: 'Only X post links work here, like x.com/you/status/\u2026' };
  return { kind: 'post', text: 'That\u2019s an X link, but not a post. Open your quote post, tap Share, then Copy link.' };
}

/* "7d left" / "5h left" / "12m left" */
export function leftText(ms) {
  const left = Math.max(0, ms);
  const d = Math.floor(left / DAY);
  const h = Math.floor((left % DAY) / 36e5);
  const m = Math.floor((left % 36e5) / 6e4);
  if (d > 0) return `${d}d left`;
  return h > 0 ? `${h}h left` : `${m}m left`;
}

/* the card is live for idle / rejected while the window is open and the campaign runs */
export const quoteEligible = (state, quote, ended, now = Date.now()) => !ended && !!state
  && (state.status === 'done' || ((state.status === 'idle' || state.status === 'rejected') && now < quote.endsAt.getTime()));

const pad2 = (n) => String(n).padStart(2, '0');
/* "{date}" / "{time}" / "{day}" / "{handle}" in the copy */
const fill = (s, vars) => String(s || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
/* "x.com/you/status/…" — the submitted link without its protocol */
const shortLink = (l) => String(l || '').replace(/^https?:\/\/(www\.)?/, '');

/* ticks once a second while `on` */
function useNow(on) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [on]);
  return now;
}

/* ---- the artwork: a megaphone and the X logo over a chest (prototype .xtile) ---- */
const X_PATH = 'M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z';

function Megaphone() {
  return (
    <svg className="gq-xmeg" viewBox="0 0 60 60" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="gqx-mg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#FFF4C2" /><stop offset=".22" stopColor="#FFD54A" /><stop offset=".55" stopColor="#F5A900" /><stop offset=".8" stopColor="#C77A00" /><stop offset="1" stopColor="#8A4D00" /></linearGradient>
        <linearGradient id="gqx-mg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#FFE07A" /><stop offset=".5" stopColor="#D99100" /><stop offset="1" stopColor="#7A4300" /></linearGradient>
        <linearGradient id="gqx-mg3" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#FFE9A0" /><stop offset=".5" stopColor="#FFC21A" /><stop offset="1" stopColor="#B86E00" /></linearGradient>
        <radialGradient id="gqx-mg4" cx=".35" cy=".4" r=".7"><stop offset="0" stopColor="#7A3E00" /><stop offset="1" stopColor="#3D1B00" /></radialGradient>
        <linearGradient id="gqx-mg5" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#FFE9A0" /><stop offset="1" stopColor="#FFE9A0" stopOpacity="0" /></linearGradient>
        <linearGradient id="gqx-mg6" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#FFE07A" /><stop offset=".45" stopColor="#E9A100" /><stop offset="1" stopColor="#8A4D00" /></linearGradient>
      </defs>
      <g transform="rotate(-28 30 32)">
        <path d="M21.6 37 L28.2 37 L27.1 51.6 Q26.9 54.4 24.2 54.4 L23.4 54.4 Q20.6 54.4 20.5 51.6 Z" fill="url(#gqx-mg6)" />
        <path d="M22.4 40 L21.8 51" stroke="#FFF1B8" strokeWidth="1.2" strokeLinecap="round" opacity=".7" />
        <path d="M28 41.2 q2.6 .6 2.4 3.4" stroke="url(#gqx-mg6)" strokeWidth="1.8" strokeLinecap="round" />
        <rect x="9.5" y="26.5" width="9" height="11" rx="2.6" fill="url(#gqx-mg2)" />
        <rect x="11" y="27.5" width="6" height="1.3" rx=".65" fill="#FFF1B8" opacity=".7" />
        <path d="M17.5 27.2L38.5 16.5v31L17.5 36.8z" fill="url(#gqx-mg1)" />
        <path d="M18.5 28.4L37 19.2v3.2L18.5 30.4z" fill="url(#gqx-mg5)" opacity=".85" />
        <ellipse cx="38.5" cy="32" rx="4.4" ry="15.6" fill="url(#gqx-mg3)" />
        <ellipse cx="39.3" cy="32" rx="2.7" ry="12.8" fill="url(#gqx-mg4)" />
        <path d="M36.6 18.6q-1.3 6 -0.9 11" stroke="#FFF6D0" strokeWidth="1" strokeLinecap="round" opacity=".8" />
        <path d="M47 25.5q3.4 6.5 0 13" stroke="#FFE08A" strokeWidth="2" strokeLinecap="round" />
        <path d="M51.5 21.5q6 10.5 0 21" stroke="#FFE08A" strokeWidth="2" strokeLinecap="round" opacity=".7" />
      </g>
    </svg>
  );
}

function XLogo() {
  return (
    <svg className="gq-xlogo" viewBox="0 0 36 36" fill="none" aria-hidden="true">
      <rect width="36" height="36" rx="9" fill="#0B0B0F" />
      <rect x=".5" y=".5" width="35" height="35" rx="8.5" stroke="rgba(255,255,255,.22)" />
      <path transform="translate(8.5 8.5) scale(.79)" fill="#fff" d={X_PATH} />
    </svg>
  );
}

export function QuoteArt() {
  return (
    <div className="gq-tile gq-xtile" aria-hidden="true">
      <img className="gq-xchest" src={chestc} alt="" />
      <Megaphone />
      <XLogo />
    </div>
  );
}

/* ---- small icons ---- */
const Tick = ({ stroke = '#fff' }) => (
  <svg viewBox="0 0 11 11" fill="none" aria-hidden="true"><path d="M2 5.6l2.3 2.3L9 3.4" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const Alert = ({ color = '#FF8A8A' }) => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.6" stroke={color} strokeWidth="1.4" /><path d="M8 4.8v3.8" stroke={color} strokeWidth="1.6" strokeLinecap="round" /><circle cx="8" cy="11" r=".95" fill={color} /></svg>
);
const Clock = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6.3" stroke="#F6D5AC" strokeWidth="1.5" /><path d="M8 4.8V8l2.2 1.4" stroke="#F6D5AC" strokeWidth="1.5" strokeLinecap="round" /></svg>
);
const Spinner = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="6" stroke="#F6D5AC" strokeWidth="1.6" strokeDasharray="26 12" strokeLinecap="round" /></svg>
);
const Bell = () => (
  <svg className="gq-xn-bell" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 2.2a4 4 0 0 0-4 4v2.3L2.8 10.6c-.3.5 0 1 .6 1h9.2c.6 0 .9-.5.6-1L12 8.5V6.2a4 4 0 0 0-4-4Z" fill="currentColor" /><path d="M6.4 12.8a1.7 1.7 0 0 0 3.2 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>
);

/* one countdown segment; the digits roll in whenever they change */
function Seg({ v, u }) {
  return (
    <span className="gq-xc-s">
      <span className="gq-xc-n"><b key={v} className="gq-roll">{pad2(v)}</b></span>
      <i>{u}</i>
    </span>
  );
}

/* the countdown pill sitting on the card's top edge; a tap flips it to the closing date */
function Countdown({ endsAt, now }) {
  const [flip, setFlip] = useState(false);
  const flipT = useRef(null);
  useEffect(() => () => clearTimeout(flipT.current), []);
  const ms = Math.max(0, endsAt.getTime() - now);
  const d = Math.floor(ms / DAY);
  const h = Math.floor((ms % DAY) / 36e5);
  const m = Math.floor((ms % 36e5) / 6e4);
  const s = Math.floor((ms % 6e4) / 1e3);
  const onFlip = () => {
    clearTimeout(flipT.current);
    setFlip(true);
    haptic('selection');
    flipT.current = setTimeout(() => setFlip(false), FLIP_MS);
  };
  return (
    <button type="button" className={`gq-xclock${ms < DAY ? ' gq-urgent' : ''}${flip ? ' gq-flip' : ''}`} onClick={onFlip} aria-label="Time left to quote and claim">
      <span className="gq-xc-face gq-xc-main">
        <span className="gq-xc-l">ENDS IN</span>
        <span className="gq-xc-segs">
          {d > 0 && <><Seg v={d} u="d" /><em>:</em></>}
          <Seg v={h} u="h" /><em>:</em><Seg v={m} u="m" /><em>:</em><Seg v={s} u="s" />
        </span>
      </span>
      <span className="gq-xc-face gq-xc-alt">
        <span className="gq-xc-l">CLOSES</span>
        <span className="gq-xc-date">{fmtDay(endsAt)}, {fmtTime(endsAt)}</span>
      </span>
    </button>
  );
}

export default function QuoteCard({ quote, state, notify = false, drawOn, ended = false, pulse = 0, onSubmit, onNotify, onClaim, innerRef }) {
  const { copy } = quote;
  const st = state.status;
  const timedState = st === 'idle' || st === 'rejected';
  const now = useNow(timedState && !ended);
  const expired = now >= quote.endsAt.getTime();
  const exp = expired && timedState;          // the window closed before a post went through
  const timed = !exp && timedState && !ended;  // the countdown shows
  const rejected = st === 'rejected';
  const claimed = st === 'claimed';

  const [fieldOpen, setFieldOpen] = useState(false); // "Paste your link" morphed into the field
  const [went, setWent] = useState(false);           // "Get started" was tapped
  const [value, setValue] = useState('');
  const [err, setErr] = useState(null);              // { kind, text } under the field
  const [busy, setBusy] = useState(false);           // the submit is in flight
  const [readyKey, setReadyKey] = useState(0);       // Submit pops when the field stops being empty
  const [dismissed, setDismissed] = useState(false); // the rejection notice was closed
  const [ring, setRing] = useState(false);           // the bell is ringing
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const fieldRef = useRef(null);
  const claimRef = useRef(null);
  const alive = useRef(true);
  const timers = useRef([]);
  useEffect(() => {
    alive.current = true;
    const pending = timers.current;
    return () => { alive.current = false; pending.forEach(clearTimeout); };
  }, []);
  const later = (fn, ms) => { timers.current.push(setTimeout(() => { if (alive.current) fn(); }, ms)); };
  const setRoot = (el) => { rootRef.current = el; if (innerRef) innerRef.current = el; };

  /* a new standing (submitted, reviewed, claimed) starts the card afresh */
  useEffect(() => {
    setFieldOpen(false); setWent(false); setValue(''); setErr(null); setDismissed(false);
  }, [st, state.submittedAt]);

  const open = (st === 'idle' && fieldOpen) || rejected;

  const openField = () => {
    setFieldOpen(true);
    later(() => { if (inputRef.current) inputRef.current.focus(); }, 120);
  };

  /* the FAB's landing: a ring of light (prototype xcflash), and the field if
     they've been to X already */
  const land = useRef(null);
  land.current = () => {
    const el = rootRef.current;
    if (el) {
      el.animate([
        { boxShadow: '0 14px 34px rgba(0,0,0,.28), 0 0 0 0 rgba(246,213,172,.7)' },
        { boxShadow: '0 14px 34px rgba(0,0,0,.28), 0 0 0 6px rgba(246,213,172,.35)', offset: 0.4 },
        { boxShadow: '0 14px 34px rgba(0,0,0,.28), 0 0 0 14px rgba(246,213,172,0)' },
      ], { duration: 1200, easing: 'ease-out' });
    }
    if (st === 'idle' && went) openField();
  };
  const firstPulse = useRef(true);
  useEffect(() => {
    if (firstPulse.current) { firstPulse.current = false; return; }
    land.current();
  }, [pulse]);

  const showError = (e) => {
    setErr(e);
    const f = fieldRef.current;
    if (f) f.animate([{ transform: 'none' }, { transform: 'translateX(-5px)', offset: 0.2 }, { transform: 'translateX(4px)', offset: 0.4 }, { transform: 'translateX(-3px)', offset: 0.6 }, { transform: 'translateX(2px)', offset: 0.8 }, { transform: 'none' }], { duration: 360 });
    haptic('notificationWarning', [10, 40, 10]);
  };
  const onChange = (e) => {
    const v = e.target.value;
    const wasOff = !value.trim() || (err && err.kind !== 'server'); // Submit was dimmed
    if (wasOff && v.trim()) setReadyKey((k) => k + 1);
    setValue(v);
    setErr(null);
  };
  /* a paste is checked on the spot (a non-link just waits for Submit) */
  const onPaste = () => {
    later(() => {
      const v = inputRef.current ? inputRef.current.value.trim() : '';
      const e = v && linkError(v);
      if (e && e.kind !== 'text') showError(e);
    }, 0);
  };
  const submit = async () => {
    if (busy) return;
    const v = value.trim();
    const e = linkError(v);
    if (e) { showError(e); return; }
    if (expired) return; // the card reads "ended" on the next tick
    setBusy(true);
    try {
      await onSubmit(v);
      if (alive.current && inputRef.current) inputRef.current.blur();
    } catch (error) {
      if (alive.current) showError({ kind: 'server', text: error && error.refused ? error.message : 'Couldn\u2019t submit the link, please try again' });
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const onKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } };

  /* the bell rings first, then the button settles into "You'll be notified" */
  const toggleNotify = () => {
    const on = !notify;
    if (on) {
      setRing(true);
      haptic('impactLight');
      later(() => setRing(false), 700);
      later(() => onNotify(true), 380);
    } else {
      onNotify(false);
    }
  };

  const claim = () => {
    if (st !== 'done') return;
    if (claimRef.current) claimRef.current.animate([{ transform: 'scale(1)' }, { transform: 'scale(.95)' }, { transform: 'scale(1)' }], { duration: 220 });
    later(onClaim, 180);
  };

  /* ---- render ---- */
  const vars = { handle: quote.handle, day: drawOn ? fmtDay(drawOn) : '13 Nov' };
  let chip;
  if (exp) chip = <span>Ended</span>;
  else if (claimed) chip = <><Tick stroke="#2A1450" /><span>{copy.claimed}</span></>;
  else if (st === 'done') chip = <><Tick stroke="#2A1450" /><span>Approved</span></>;
  else if (st === 'review') chip = <><span className="gq-pdot" /><span>{copy.reviewTitle}</span></>;
  else if (rejected) chip = <span>Rejected</span>;
  else chip = <><img src={chestc} alt="" /><span>{copy.chip}</span></>;

  const reason = rejected ? (QUOTE_REJECT[state.rejectReason] || QUOTE_REJECT.invalid) : null;
  const reasonUrl = reason && reason.url ? (reason.url === 'profile' ? quote.profileUrl : quote.postUrl) : null;
  const by = state.submittedAt ? new Date(state.submittedAt.getTime() + DAY) : null;

  const cls = [
    'gq-card gq-xcard',
    timed ? 'gq-xtimed' : '', open ? 'gq-xopen' : '', exp ? 'gq-xexp' : '',
    st === 'done' || claimed ? 'gq-done' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} data-st={st} ref={setRoot}>
      {timed && <Countdown endsAt={quote.endsAt} now={now} />}
      <QuoteArt />
      <div className="gq-t gq-ttl">{copy.title}</div>
      <div className="gq-txt"><div className="gq-desc">{copy.text}</div></div>
      <div className="gq-lockchip gq-svchip gq-xchip">{chip}</div>
      <div className="gq-divider" />

      {/* why the post was rejected, with the way to fix it */}
      {rejected && !exp && !dismissed && (
        <div className="gq-xrej">
          <Alert />
          <span className="gq-xr-txt">
            <b>{fill(reason.title, vars)}</b>
            <small>{fill(reason.text, vars)}</small>
            {reason.action && <a className="gq-xr-act" href={reasonUrl} target="_blank" rel="noopener noreferrer">{fill(reason.action, vars)}</a>}
          </span>
          <button type="button" className="gq-xclose" onClick={() => setDismissed(true)} aria-label="Dismiss">
            <svg viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2 2l6 6M8 2L2 8" stroke="#FFD0D0" strokeWidth="1.6" strokeLinecap="round" /></svg>
          </button>
        </div>
      )}

      {/* idle / rejected: Get started, and Paste your link → the inline field */}
      {timedState && !exp && (
        <div className="gq-xctas">
          <a className="gq-xmain" href={quote.postUrl} target="_blank" rel="noopener noreferrer" onClick={() => setWent(true)}>{copy.go}</a>
          <div className="gq-xpaste">
            <button type="button" className="gq-xsec" onClick={openField} tabIndex={open ? -1 : 0} aria-hidden={open}><span>{copy.paste}</span></button>
            <div className={`gq-xfield${err ? ' gq-bad' : ''}`} ref={fieldRef}>
              <input
                ref={inputRef}
                type="url"
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                placeholder={copy.placeholder}
                value={value}
                onChange={onChange}
                onPaste={onPaste}
                onKeyDown={onKeyDown}
                tabIndex={open ? 0 : -1}
                aria-label={copy.placeholder}
              />
              <button type="button" className={`gq-xsub${readyKey ? ' gq-ready' : ''}`} key={readyKey} disabled={!value.trim() || busy || !!(err && err.kind !== 'server')} aria-busy={busy} onClick={submit}>{copy.submit}</button>
            </div>
          </div>
          {err && <div className="gq-xhint" role="alert"><Alert /><span>{err.text}</span></div>}
        </div>
      )}

      {st === 'review' && (
        <div className="gq-xstat gq-rv">
          <div className="gq-xi"><Spinner /></div>
          <div>
            <b>{copy.reviewTitle}</b>
            <span className="gq-x24">{by ? fill(copy.reviewBy, { date: fmtDay(by), time: fmtTime(by) }) : copy.reviewText}</span>
            {state.link && <span>{shortLink(state.link)}</span>}
          </div>
        </div>
      )}

      {exp && (
        <>
          <div className="gq-xstat gq-ex">
            <div className="gq-xi"><Clock /></div>
            <div>
              <b>{copy.endedTitle}</b>
              <span>{fill(copy.endedText, { date: fmtDay(quote.endsAt) })}</span>
            </div>
          </div>
          <button type="button" className={`gq-xnotify${notify ? ' gq-set' : ''}${ring ? ' gq-ring' : ''}`} onClick={toggleNotify} aria-pressed={notify}>
            {notify
              ? <svg className="gq-xn-tick" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.3l2.4 2.4L9.6 3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              : <Bell />}
            <span>{notify ? copy.notified : copy.notify}</span>
          </button>
          <div className="gq-xnote">{notify ? copy.noteSet : copy.note}</div>
        </>
      )}

      {(st === 'done' || claimed) && (
        <>
          <div className="gq-xstat gq-ok">
            <div className="gq-xi"><svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.4l3 3 6-6.4" stroke="#2A1450" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
            <div>
              <b>{copy.okTitle}</b>
              <span>{claimed ? fill(copy.claimedText, vars) : copy.okText}</span>
            </div>
          </div>
          <button type="button" className="gq-xclaim" ref={claimRef} onClick={claim} disabled={claimed}>
            {!claimed && <img src={chestc} alt="" />}
            <span>{claimed ? copy.claimed : copy.claim}</span>
          </button>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
   The hub's floating pill (prototype #xfab): shows while the Quote &
   Claim card is scrolled out of view and the task is live for the user
   (idle / rejected before the window closes, or a chest ready to claim),
   folds to its icon while the page scrolls down, and sits above the
   sticky footer when there is one. `cardRef` is the card to watch;
   `show` whether the hub is on screen; `onClick` brings the card up.
   Rendered in a zoomed wrapper, fixed over the page like the toast.
   ------------------------------------------------------------------ */
export function QuoteFab({ quote, state, ended = false, show = true, sticky = false, cardRef, onClick }) {
  const { copy } = quote;
  const st = state.status;
  const now = useNow(show);
  const eligible = quoteEligible(state, quote, ended, now);
  const [seen, setSeen] = useState(true); // the card is (≥35%) in view
  const ref = useRef(null);
  const scroll = useRef({ last: 0, t: null });

  useEffect(() => {
    const el = cardRef && cardRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setSeen(false); return undefined; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => setSeen(e.isIntersecting)), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, [cardRef, eligible]);

  /* folds while the page scrolls down; back out once the scrolling stops */
  useEffect(() => {
    const s = scroll.current;
    const onScroll = () => {
      const f = ref.current;
      if (!f) return;
      const y = window.scrollY;
      if (y > s.last + 4) f.classList.add('gq-mini');
      s.last = y;
      clearTimeout(s.t);
      s.t = setTimeout(() => { if (ref.current) ref.current.classList.remove('gq-mini'); }, 900);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); clearTimeout(s.t); };
  }, []);

  const on = show && eligible && !seen;
  const ready = st === 'done';
  const sub = ready ? copy.fabReadySub : `${st === 'rejected' ? copy.fabRetry : copy.fabSub} \u00b7 ${leftText(quote.endsAt.getTime() - now)}`;
  return (
    <div className="gq-xfab-pos" style={{ bottom: sticky ? 116 : 22 }}>
      <button type="button" className={`gq-xfab${on ? ' gq-show' : ''}${ready ? ' gq-ready' : ''}`} ref={ref} onClick={onClick} aria-label="Quote and Claim: win an extra chest" aria-hidden={!on} tabIndex={on ? 0 : -1}>
        <span className="gq-xf-ring" />
        <span className="gq-xf-ic">
          <img className="gq-xf-chest" src={chestc} alt="" />
          <span className="gq-xf-x"><svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#fff" d={X_PATH} /></svg></span>
        </span>
        <span className="gq-xf-t">
          <b>{ready ? copy.fabReady : copy.title}</b>
          <span>{sub}</span>
        </span>
      </button>
    </div>
  );
}
